import { message } from 'antd';
import { isBefore, parseISO, startOfDay } from 'date-fns';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionBanners } from '../../../../permissions/PermissionBanners';
import { PermissionCategories } from '../../../../permissions/PermissionCategories';
import { PermissionComponents } from '../../../../permissions/PermissionComponents';
import bannerStore from '../../../../stores/bannerStore';
import categoryStore from '../../../../stores/categoryStore';
import componentsStore from '../../../../stores/componentsStore';
import { formattedDates } from '../../../../utils/functions';
import { ComponentesSelect, YesOrNot } from '../../../../utils/selects';
import ActionButton from '../../../atoms/ButtonComponent';
import DatePickerRangeComponet from '../../../atoms/DatePickerRangeComponet';
import InputComponet from '../../../atoms/InputComponet';
import PermissionComponent from '../../../atoms/PermissionComponent';
import SelectComponent from '../../../atoms/SelectComponent';
import SwitchComponent from '../../../atoms/SwitchComponent';
import ContentBox from '../../../molecules/ContentBox';
import { MobileIcon, WebIcon } from '../../../molecules/IconComponent';
import LoadingComponent from '../../../molecules/LoadingComponent';
import Permission from '../../../molecules/Permission';
import PreviewComponents from '../../../organisms/ComponentesHome';
import { validationSchemaBanner, validationSchemaCategory } from './schema';

const CreateComponentKernel: React.FC = observer(() => {
    const { uuid } = useParams<{ uuid: string }>();
    const { componentProps, loading } = componentsStore;
    const { bannerListSelected } = bannerStore;
    const navigate = useNavigate();
    const { selectCategory } = categoryStore;
    const [typeSelected, setTypeSelected] = useState<any>();
    const [selectedComponent, setSelectedComponent] = useState<any>();
    const [isMobile, setMobile] = useState<boolean>(false);

    useEffect(() => {
        return () => {
            componentsStore.reset();
        };
    }, [uuid]);

    const initialValues: any = {
        name: '',
        order: '',
        active: false,
        type: '',
        date: '',
        banners: '',
        category: '',
        mobile: '',
    };

    // Faz o filtro da tabela
    const handleSubmit = async (values: any) => {
        const formattedDate = formattedDates(values?.date, 'YYYY-MM-DD');
        const dateInitial = formattedDate[0];
        const dateFinal = formattedDate[1];

        const today = startOfDay(new Date());
        const finalDateParsed = parseISO(`${dateFinal}T23:59:59`);

        if (isBefore(finalDateParsed, today)) {
            message.error('A data final não pode ser menor que hoje.');
            return; // Interrompe o fluxo caso a validação falhe
        }

        let response;

        const createProps = {
            type: values?.type?.value,
            name: values?.name,
            order: parseInt(values?.order, 10),
            mobile: values?.mobile?.label === 'Sim' ? true : false,
            app: false,
            banners: [{
                uuid: values?.banners?.value
            }],
            category: {
                uuid: values?.category?.value
            },
            minDate: `${dateInitial}T00:00:00`,
            maxDate: `${dateFinal}T23:59:59`,
            active: values?.active ? values?.active : false,
            uuid: componentProps?.uuid,
        }

        if (values?.type?.value === "BANNER_CAROUSEL") {
            //@ts-ignore
            delete createProps.category
        }

        if (values?.type?.value === "PRODUCT_CAROUSEL") {
            //@ts-ignore
            delete createProps?.banners
        }

        if (uuid) {
            response = await componentsStore.editComponent(createProps, uuid);
        } else {
            response = await componentsStore.createComponet(createProps);
        }

        if (!response.error) listRouter();
    };


    const listRouter = () => {
        navigate(`/componentes`);
    }

    return (
        <>
            {!loading &&
                <Permission
                    permissionKeys={[PermissionComponents.POST, PermissionComponents.PUT, PermissionComponents.GET_UUID]}
                    fallback={<PermissionComponent />}
                >
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={typeSelected?.value === "BANNER_CAROUSEL" ? validationSchemaBanner : validationSchemaCategory}
                    >
                        <Form>
                            <ContentBox borderCustom='rounded-b-lg' className="mb-4 p-6" title="Cadastro de Componente" description='Tela para cadastro de Componentes' actions={
                                <>
                                    <SwitchComponent name="active" checkedChildren="Ativo" unCheckedChildren="Inativo" />
                                </>
                            }>
                                <div className="flex w-full gap-4 pb-4">
                                    <InputComponet label="Nome" name="name" id="name" />
                                    <SelectComponent label="Responsivo" name="mobile" id="mobile" options={YesOrNot} />
                                    <SelectComponent onChange={(value, option) => setTypeSelected(option)} label="Tipo" name="type" options={ComponentesSelect} />
                                </div>

                                <div className="flex gap-4 grid grid-cols-12 pb-4">
                                    <div className="col-span-8">
                                        <DatePickerRangeComponet label="Data Inicial e Final" name="date" />
                                    </div>
                                    <div className="col-span-4">
                                        <InputComponet label="Ordenação" name="order" id="order" />
                                    </div>
                                </div>

                                <div className="flex w-full pb-4">
                                    <Permission permissionKeys={[PermissionCategories.GET]} inputType name="Categoria">
                                        {typeSelected && typeSelected?.value === "PRODUCT_CAROUSEL" &&
                                            <SelectComponent onChange={(value, option) => setSelectedComponent(option?.option)} label="Categoria" name="category" options={selectCategory} />
                                        }
                                    </Permission>

                                    <Permission permissionKeys={[PermissionBanners.GET_UUID]} inputType name="Banners">
                                        {typeSelected && typeSelected?.value === "BANNER_CAROUSEL" &&
                                            <SelectComponent onChange={(value, option) => setSelectedComponent(option?.option)} label="Banners" name="banners" options={bannerListSelected} />
                                        }
                                    </Permission>
                                </div>
                            </ContentBox>

                            <ContentBox className="mb-4 p-6" title="Preview dos Components" description='Visualização dos Componentes' actions={
                                <div className="flex gap-3">
                                    <div onClick={() => setMobile(true)} className={`p-2 cursor-pointer rounded ${isMobile ? 'border-2 border-blue-500' : 'border border-slate-200'}`}>
                                        <MobileIcon />
                                    </div>
                                    <div onClick={() => setMobile(false)} className={`p-2 cursor-pointer rounded ${!isMobile ? 'border-2 border-blue-500' : 'border border-slate-200'}`}>
                                        <WebIcon />
                                    </div>
                                </div>
                            }>

                                <PreviewComponents isMobile={isMobile} tag={typeSelected?.value === 'BANNER_CAROUSEL' ? selectedComponent?.tag : 'PRODUCT_CAROUSEL'} banners={typeSelected?.type === 'BANNER_CAROUSEL' ? selectedComponent?.files[0] : selectedComponent} bannerPage={false} />
                            </ContentBox>

                            <div className="flex justify-end">
                                <Permission permissionKeys={[PermissionComponents.PUT, PermissionComponents.POST]} buttonType>
                                    <ActionButton htmlType="submit" actionType="primary">{uuid ? 'Atualizar' : 'Cadastrar'}</ActionButton>
                                </Permission>
                            </div>
                        </Form>
                    </Formik >
                </Permission>
            }
        </>
    );
});

export default CreateComponentKernel;